/* =========================================================
   OVERTOP — Variant Media Filter v6.0.0
   ---------------------------------------------------------
   The ONE place that decides which images a colour shows, for
   Horizon's product gallery (the phone slideshow, its thumbnails,
   the zoom dialog's thumbnails and the wide-screen grid).

   For the colour chosen: its own cover first (the variant's image
   set in Shopify admin), then that colour's other images in the
   product's order, then any image that names no colour; never
   another colour's photo. Thumbnails show the same set, the one in
   view marked.

   Which colour an image is, strongest first:
     1. it is a variant's image (variants[].featured_media);
     2. its alt text names one colour ("Black", "Black — side");
     3. its file name names one colour (UHS11Black2.png,
        UH_S11_Grey_3.png);
     4. if no image is named by 2 or 3 and the media are grouped
        (each cover followed by its own images), the images after a
        cover up to the next cover. Otherwise an image no rule can
        place is shared: shown after the colour's own, never before.
   (v4–v5 used rule 4 alone, so a product whose covers came first —
   Black·Grey·Brown, then the rest mixed — gave Black and Grey one
   image each and Brown everything else, and every slide stayed in.)

   A colour tap (the sticky bar's chips click the main picker's
   radios, so both arrive here as one `change`) slides the phone
   gallery at once, from the product data already in the page, in
   the tap's direction: the new colour's cover is set beside the
   slide in view, slid to, and then the gallery is laid out for the
   new colour. Rapid taps end on the last colour. Horizon's
   media-gallery asks OT_GALLERY_KEEPS before replacing itself on
   the server's answer: on a phone, same media, it keeps this one.
   ========================================================= */

(function () {
  'use strict';

  var VERSION = '6.0.0';
  window.__OT_VARIANT_MEDIA_FILTER_VERSION = VERSION;

  var model = null;          // { colours[], colourOfVariant{}, cover{}, sets{}, fileToId{} }
  var shownColour = null;    // the colour the gallery is laid out for
  var wantColour = null;     // the last colour asked for
  var busy = false;          // a slide between colours is running
  var loadPromise = null;

  function getHandle() {
    var m = window.location.pathname.match(/\/products\/([^/?#]+)/);
    return m ? decodeURIComponent(m[1]) : null;
  }
  function rootUrl() {
    try {
      if (window.Shopify && window.Shopify.routes && window.Shopify.routes.root) {
        return String(window.Shopify.routes.root || '/').replace(/\/$/, '');
      }
    } catch (err) {}
    return '';
  }
  function getProductDataCache() {
    if (window.OT_PRODUCT_DATA_CACHE && window.OT_PRODUCT_DATA_CACHE.fetchByHandle) return window.OT_PRODUCT_DATA_CACHE;
    var cache = {
      products: {},
      promises: {},
      fetchByHandle: function (handle) {
        handle = String(handle || '').trim();
        if (!handle) return Promise.resolve(null);
        if (cache.products[handle]) return Promise.resolve(cache.products[handle]);
        if (cache.promises[handle]) return cache.promises[handle];
        cache.promises[handle] = fetch(rootUrl() + '/products/' + encodeURIComponent(handle) + '.js', {
          credentials: 'same-origin', headers: { 'Accept': 'application/json' }
        }).then(function (res) {
          if (!res.ok) throw new Error('HTTP ' + res.status);
          return res.json();
        }).then(function (product) {
          cache.products[handle] = product;
          if (product && product.handle) cache.products[product.handle] = product;
          return product;
        }).catch(function (err) { delete cache.promises[handle]; throw err; });
        return cache.promises[handle];
      },
      getByHandle: function (handle) { return cache.products[String(handle || '').trim()] || null; }
    };
    window.OT_PRODUCT_DATA_CACHE = cache;
    return cache;
  }

  /* ---------- which colour an image is ---------- */
  function fileOf(src) { return String(src || '').split('?')[0].split('/').pop() || ''; }
  function norm(x) { return String(x || '').toLowerCase().replace(/gray/g, 'grey'); }
  function tokensOf(text) {
    return String(text || '').replace(/\.[a-z0-9]+$/i, '').split(/[^A-Za-z]+/)
      .reduce(function (acc, t) { return acc.concat(t.replace(/([a-z])([A-Z])/g, '$1 $2').split(' ')); }, [])
      .filter(Boolean).map(norm);
  }
  // the one colour a text names, or null when it names none or more than one
  function colourNamed(text, colours) {
    if (!text) return null;
    var toks = tokensOf(text);
    var hits = colours.filter(function (c) {
      var words = tokensOf(c);
      if (!words.length) return false;
      for (var i = 0; i + words.length <= toks.length; i++) {
        var ok = true;
        for (var j = 0; j < words.length; j++) if (toks[i + j] !== words[j]) { ok = false; break; }
        if (ok) return true;
      }
      return false;
    });
    return hits.length === 1 ? hits[0] : null;
  }

  // the store's data in one shape: the page's own (snippets/ot-scripts.liquid) or the product's .js
  function readProduct(p) {
    if (!p || !p.media || !p.variants) return null;
    var names = (p.options || []).map(function (o) { return typeof o === 'string' ? o : o && o.name; });
    var ix = names.findIndex(function (n) { return /colou?r/i.test(n || ''); });
    if (ix < 0) ix = names.length === 1 ? 0 : -1;
    if (ix < 0) return null;
    var media = p.media.map(function (m) {
      return { id: String(m.id), position: Number(m.position), alt: m.alt || '', file: fileOf(m.src || (m.preview_image && m.preview_image.src)) };
    }).sort(function (a, b) { return a.position - b.position; });
    var variants = p.variants.map(function (v) {
      var fm = v.featured_media && (v.featured_media.id || v.featured_media);
      return { id: String(v.id), colour: (v.options || [])[ix], cover: fm ? String(fm) : null };
    });
    return { media: media, variants: variants };
  }
  function buildModel(p) {
    var d = readProduct(p);
    if (!d) return null;
    var colours = [];
    d.variants.forEach(function (v) { if (v.colour != null && colours.indexOf(v.colour) === -1) colours.push(v.colour); });
    if (colours.length < 2) return null;              // one colour: nothing to choose between

    var colourOfVariant = {}, cover = {}, owner = {};
    d.variants.forEach(function (v) {
      colourOfVariant[v.id] = v.colour;
      if (v.cover && !cover[v.colour]) cover[v.colour] = v.cover;
      if (v.cover) owner[v.cover] = v.colour;           // 1. a variant's own image
    });
    var named = 0;
    d.media.forEach(function (m) {
      if (owner[m.id]) return;
      var c = colourNamed(m.alt, colours) || colourNamed(m.file, colours);   // 2. alt text, 3. file name
      if (c) { owner[m.id] = c; named++; }
    });
    if (!named) {
      // 4. grouped media: each cover followed by its own images, up to the next cover
      var covers = d.media.filter(function (m) { return owner[m.id]; });
      var grouped = covers.length >= 2 && covers.every(function (m, i) {
        var next = covers[i + 1];
        return d.media.some(function (x) { return !owner[x.id] && x.position > m.position && (!next || x.position < next.position); });
      });
      if (grouped) {
        var current = null;
        d.media.forEach(function (m) { if (owner[m.id]) current = owner[m.id]; else if (current) owner[m.id] = current; });
      }
    }
    var sets = {}, fileToId = {};
    d.media.forEach(function (m) { fileToId[m.file] = m.id; });
    colours.forEach(function (c) {
      var own = d.media.filter(function (m) { return owner[m.id] === c && m.id !== cover[c]; }).map(function (m) { return m.id; });
      var shared = d.media.filter(function (m) { return !owner[m.id]; }).map(function (m) { return m.id; });
      sets[c] = (cover[c] ? [cover[c]] : []).concat(own, shared);
    });
    return { colours: colours, colourOfVariant: colourOfVariant, cover: cover, sets: sets, fileToId: fileToId };
  }
  function loadModel() {
    if (model) return Promise.resolve(model);
    var inline = window.OT_PRODUCT_MEDIA;
    if (inline && (!getHandle() || inline.handle === getHandle())) {
      model = buildModel(inline);
      return Promise.resolve(model);
    }
    if (loadPromise) return loadPromise;
    var handle = getHandle();
    if (!handle) return Promise.resolve(null);
    loadPromise = getProductDataCache().fetchByHandle(handle)
      .then(function (p) { model = buildModel(p); return model; })
      .catch(function () { return null; });
    return loadPromise;
  }
  // for anything else that wants the same answer (ot-image-preload)
  window.OT_MEDIA_OF_VARIANT = function (variantId) {
    if (!model) return null;
    var c = model.colourOfVariant[String(variantId)];
    return c != null ? model.sets[c].slice() : null;
  };

  /* ---------- the gallery's parts ---------- */
  function mainGallery() {
    var all = document.querySelectorAll('media-gallery');
    for (var i = 0; i < all.length; i++) {
      if (!all[i].closest('quick-add-component, quick-add-dialog, product-card, .product-card, .product-recommendations')) return all[i];
    }
    return null;
  }
  function slideshowOf(g) { return g && g.querySelector('slideshow-component'); }
  function scrollerOf(ss) { return ss && (ss.querySelector('slideshow-slides') || ss); }
  function phoneSlideshow(g) {
    var ss = slideshowOf(g);
    return ss && ss.getClientRects().length && ss.offsetWidth > 0 ? ss : null;
  }
  function mediaIdOf(el) {
    var m = el && el.querySelector('[data-media-id]');
    return m ? m.getAttribute('data-media-id') : null;
  }
  function slidesOf(ss) {
    var sc = scrollerOf(ss);
    return sc ? Array.prototype.filter.call(sc.children, function (n) { return n.tagName === 'SLIDESHOW-SLIDE'; }) : [];
  }
  function slideFor(ss, mediaId) {
    var list = slidesOf(ss);
    for (var i = 0; i < list.length; i++) if (mediaIdOf(list[i]) === String(mediaId)) return list[i];
    return null;
  }
  // a grid item or a thumbnail's own display rule would outweigh the hidden attribute
  function style() {
    if (document.getElementById('ot-vmf-style')) return;
    var st = document.createElement('style');
    st.id = 'ot-vmf-style';
    st.textContent = '.media-gallery__grid > li[hidden],.slideshow-controls__thumbnail[hidden],.dialog-thumbnails-list__thumbnail[hidden]{display:none !important}';
    (document.head || document.documentElement).appendChild(st);
  }
  function setHidden(el, hide) {
    if (!el) return;
    if (hide) {
      if (!el.hasAttribute('hidden')) el.setAttribute('hidden', '');
      el.removeAttribute('reveal');
      el.setAttribute('aria-hidden', 'true');
    } else if (el.hasAttribute('hidden')) {
      el.removeAttribute('hidden');
    }
  }
  function eager(slide) {
    if (!slide) return;
    slide.querySelectorAll('img').forEach(function (img) {
      if (img.loading !== 'eager') img.loading = 'eager';
      if (img.decode) img.decode().catch(function () {});
    });
  }
  function offsetIn(sc, el) { return el.getBoundingClientRect().left - sc.getBoundingClientRect().left; }
  function jumpTo(sc, el) {
    var d = offsetIn(sc, el);
    if (Math.abs(d) > 0.5) sc.scrollTo({ left: sc.scrollLeft + d, behavior: 'instant' });
  }
  function inView(ss) {
    var sc = scrollerOf(ss), list = slidesOf(ss);
    if (!sc) return null;
    var a = sc.getBoundingClientRect(), cx = a.left + a.width / 2;
    for (var i = 0; i < list.length; i++) {
      var r = list[i].getBoundingClientRect();
      if (r.width && r.left <= cx && r.right >= cx) return list[i];
    }
    return null;
  }
  // put `els` first, in that order, inside `box`, and only touch the DOM when the order differs
  function orderFirst(box, els) {
    var kids = Array.prototype.slice.call(box.children);
    var same = els.every(function (el, i) { return kids[i] === el; });
    if (same) return;
    var anchor = box.firstChild;
    els.forEach(function (el) { box.insertBefore(el, anchor); anchor = el.nextSibling; });
  }

  /* ---------- lay the gallery out for one colour, at once ---------- */
  function layThumbnails(colour) {
    var set = model.sets[colour];
    var box = document.querySelector('media-gallery .slideshow-controls__thumbnails');
    var g = mainGallery();
    if (g) box = g.querySelector('.slideshow-controls__thumbnails') || box;
    if (box) {
      var buttons = Array.prototype.slice.call(box.querySelectorAll('.slideshow-controls__thumbnail'));
      var byId = {};
      buttons.forEach(function (b) {
        var img = b.querySelector('img');
        var id = model.fileToId[fileOf(img && (img.getAttribute('src') || img.src))];
        if (id) byId[id] = b;
      });
      var want = set.map(function (id) { return byId[id]; }).filter(Boolean);
      orderFirst(box, want);
      buttons.forEach(function (b) { if (want.indexOf(b) === -1) setHidden(b, true); });
      want.forEach(function (b, i) {
        setHidden(b, false);
        // Horizon's thumbnail selects a slide by its place among the slides shown
        if (b.getAttribute('on:click') !== '/select/' + i) b.setAttribute('on:click', '/select/' + i);
        b.setAttribute('aria-selected', i === 0 ? 'true' : 'false');
      });
    }
    // the zoom dialog's strip: the same colour only
    document.querySelectorAll('.dialog-thumbnails-list__thumbnail').forEach(function (b) {
      var img = b.querySelector('img');
      var id = model.fileToId[fileOf(img && (img.getAttribute('src') || img.src))];
      if (id) setHidden(b, set.indexOf(id) === -1);
    });
  }
  function layGrid(colour, g) {
    var grid = g && g.querySelector('.media-gallery__grid');
    if (!grid) return;
    var set = model.sets[colour];
    var items = Array.prototype.filter.call(grid.children, function (n) { return n.tagName === 'LI'; });
    var byId = {};
    items.forEach(function (li) { var id = mediaIdOf(li); if (id) byId[id] = li; });
    var want = set.map(function (id) { return byId[id]; }).filter(Boolean);
    orderFirst(grid, want);
    items.forEach(function (li) { setHidden(li, want.indexOf(li) === -1); });
  }
  function lay(colour) {
    style();
    var g = mainGallery();
    if (!g || !model || !model.sets[colour]) return;
    var ss = slideshowOf(g);
    if (ss) {
      var sc = scrollerOf(ss);
      var set = model.sets[colour];
      var want = set.map(function (id) { return slideFor(ss, id); }).filter(Boolean);
      if (want.length) {
        orderFirst(sc, want);
        slidesOf(ss).forEach(function (s) { setHidden(s, want.indexOf(s) === -1); });
        want.forEach(function (s) { setHidden(s, false); });
        jumpTo(sc, want[0]);
        eager(want[0]);
        // Horizon reads its slides and thumbnails again on the mutation; then the cover is current
        Promise.resolve().then(function () { return null; }).then(function () {
          try { ss.current = 0; } catch (e) {}
        });
      }
    }
    layThumbnails(colour);
    layGrid(colour, g);
    shownColour = colour;
  }

  /* ---------- slide from one colour to another, in the tap's direction ---------- */
  function slideTo(sc, el) {
    return new Promise(function (resolve) {
      var start = performance.now();
      sc.scrollTo({ left: sc.scrollLeft + offsetIn(sc, el), behavior: 'smooth' });
      (function wait() {
        if (Math.abs(offsetIn(sc, el)) < 1 || performance.now() - start > 1400) return resolve();
        requestAnimationFrame(wait);
      })();
    });
  }
  function change(from, to) {
    var ss = phoneSlideshow(mainGallery());
    var cover = ss && slideFor(ss, model.cover[to] || model.sets[to][0]);
    var cur = ss && inView(ss);
    if (!ss || !cover || !cur || cur === cover || from == null) { lay(to); return Promise.resolve(); }
    var sc = scrollerOf(ss);
    var forward = model.colours.indexOf(to) > model.colours.indexOf(from);
    busy = true;
    eager(cover);
    setHidden(cover, false);
    var before = offsetIn(sc, cur);
    if (forward) cur.after(cover); else cur.before(cover);
    // moving back puts the cover before the slide in view: keep that slide where it is (Safari does not)
    var shift = offsetIn(sc, cur) - before;
    if (Math.abs(shift) > 0.5) sc.scrollTo({ left: sc.scrollLeft + shift, behavior: 'instant' });
    layThumbnails(to);
    return slideTo(sc, cover).then(function () { lay(to); busy = false; });
  }
  function ensure() {
    if (busy || !model || wantColour == null || wantColour === shownColour) return;
    var from = shownColour, to = wantColour;
    change(from, to).then(ensure, function () { busy = false; ensure(); });
  }
  function showVariant(variantId) {
    if (!variantId) return;
    loadModel().then(function (m) {
      if (!m) return;
      var c = m.colourOfVariant[String(variantId)];
      if (c == null) return;
      wantColour = c;
      ensure();
    });
  }
  window.OT_VARIANT_SHOW = showVariant;

  function mediaIdsOf(gallery) {
    return Array.prototype.map.call(gallery.querySelectorAll('slideshow-slide [data-media-id]'), function (el) {
      return el.getAttribute('data-media-id');
    }).sort().join(',');
  }
  function variantFromEvent(event) {
    var variant = event && event.detail && (event.detail.resource || event.detail.variant);
    return variant && variant.id ? String(variant.id) : null;
  }
  // Horizon's media-gallery asks before it replaces itself with the server's copy (assets/media-gallery.js)
  window.OT_GALLERY_KEEPS = function (gallery, fresh, event) {
    try {
      if (!model || !gallery || gallery !== mainGallery() || !phoneSlideshow(gallery)) return false;
      var d = event && event.detail && event.detail.data;
      if (d && d.newProduct) return false;                    // a combined listing loads another product
      if (mediaIdsOf(gallery) !== mediaIdsOf(fresh)) return false;
      var id = variantFromEvent(event);
      if (id && model.colourOfVariant[id] != null && model.colourOfVariant[id] === wantColour) ensure();
      return true;
    } catch (e) { return false; }
  };

  /* ---------- reading the tap ---------- */
  function variantFromChange(target) {
    if (!target || !target.closest || !target.closest('variant-picker')) return null;
    if (target.closest('quick-add-component, quick-add-dialog, product-card, .product-card')) return null;
    if (target.tagName === 'SELECT') {
      var o = target.options[target.selectedIndex];
      return o && o.dataset ? o.dataset.variantId || null : null;
    }
    return target.dataset ? target.dataset.variantId || null : null;
  }
  function variantFromChip(target) {
    var chip = target && target.closest && target.closest('#otStickyAtcBar .ot-sw[data-value]');
    if (!chip) return null;
    var radios = document.querySelectorAll('variant-picker input[type="radio"][data-variant-id]');
    for (var i = 0; i < radios.length; i++) {
      if (radios[i].value === chip.getAttribute('data-value') && !radios[i].closest('product-card, quick-add-component')) return radios[i].dataset.variantId;
    }
    return null;
  }
  function checkedVariant() {
    var q = new URL(location.href).searchParams.get('variant');
    var checked = document.querySelector('variant-picker input:checked[data-variant-id], variant-picker option:checked[data-variant-id]');
    if (checked && checked.dataset && checked.dataset.variantId) return checked.dataset.variantId;
    return q;
  }
  function preloadVariant(variantId, priority) {
    try { if (window.OT_IMAGE_PRELOAD_VARIANT) window.OT_IMAGE_PRELOAD_VARIANT(variantId, priority || 'high'); } catch (err) {}
  }
  // every colour's images, loaded after the page settles: covers first, the rest after
  function preloadColours() {
    var ss = slideshowOf(mainGallery());
    if (!ss || !model) return;
    model.colours.forEach(function (c) { eager(slideFor(ss, model.cover[c])); });
    var idle = window.requestIdleCallback || function (fn) { return setTimeout(fn, 600); };
    idle(function () { slidesOf(ss).forEach(eager); }, { timeout: 2500 });
  }

  function boot() {
    if (!getHandle()) return;

    loadModel().then(function (m) {
      if (!m) return;
      var c = m.colourOfVariant[String(checkedVariant())];
      if (c == null) c = m.colours[0];
      wantColour = c;
      lay(c);
      var whenLoaded = function () { setTimeout(preloadColours, 300); };
      if (document.readyState === 'complete') whenLoaded(); else window.addEventListener('load', whenLoaded, { once: true });
    });

    // the tap itself: slide now (capture, before Horizon's picker starts its server round trip)
    document.addEventListener('change', function (event) {
      var variantId = variantFromChange(event.target);
      if (variantId) showVariant(variantId);
    }, true);

    document.addEventListener('pointerdown', function (event) {
      var v = variantFromChip(event.target);
      if (!v || !model) return;
      preloadVariant(v, 'high');
      eager(slideFor(slideshowOf(mainGallery()), model.cover[model.colourOfVariant[v]]));
    }, { passive: true, capture: true });

    // the server's answer: on a wide screen Horizon has put its own gallery in; lay it out for the colour
    document.addEventListener('variant:update', function (event) {
      var id = variantFromEvent(event) || checkedVariant();
      loadModel().then(function (m) {
        if (!m || busy) return;
        var c = m.colourOfVariant[String(id)];
        if (c == null) return;
        wantColour = c;
        if (!phoneSlideshow(mainGallery())) lay(c); else ensure();
      });
    });

    document.addEventListener('shopify:section:load', function () {
      setTimeout(function () { model = null; loadPromise = null; shownColour = null; boot(); }, 120);
    });
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', boot);
  } else {
    boot();
  }
})();
